import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import AppShell from "@/components/AppShell";
import api, { downloadFile } from "@/lib/api";
import Icon from "@/components/Icon";
import JourneyPosition from "@/components/JourneyPosition";
import ReconcileCareer from "@/components/ReconcileCareer";
import TalentaCTA from "@/components/TalentaCTA";
import { PartnerReport, PartnerTryouts } from "@/components/PartnerSections";
import MotivasiQuote from "@/components/MotivasiQuote";
import { RecognizeSection, PosttestSection, InAppTryouts, BagianHeader, EmptyBox } from "@/components/LearningSections";
import SystemInsight, { TryoutInsight } from "@/components/SystemInsight";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import { RadarChart, Radar, PolarGrid, PolarAngleAxis, BarChart, Bar, XAxis, YAxis, ResponsiveContainer, Cell } from "recharts";
import { Sparkles, TrendingUp, AlertCircle, Compass, ClipboardCheck, ArrowRight, Download, Milestone, ShieldCheck, GraduationCap, BookOpen, Briefcase, Star, Route, Lightbulb, X, CheckCircle2, Mail, Printer, UserRound, ExternalLink, FileText } from "lucide-react";

const JALUR_ICON = { akademik: GraduationCap, sipil: Milestone, taruna: ShieldCheck };
const JALUR_COLOR = { akademik: "#E25E3E", sipil: "#4A6B53", taruna: "#1A1C1A" };

const KOGNITIF_INFO = {
  Numerik: "kemampuan berhitung & mengolah angka",
  Verbal: "kemampuan memahami kata & bahasa",
  Spasial: "kemampuan membayangkan bentuk & ruang",
  Logika: "kemampuan menalar pola & sebab–akibat",
  Memori: "kemampuan mengingat informasi",
};
const BELAJAR_INFO = {
  Konsistensi: "keteraturan & kedisiplinan belajar rutin",
  Fokus: "kemampuan berkonsentrasi tanpa mudah terganggu",
  Strategi: "penggunaan metode & cara belajar yang efektif",
  "Manajemen Waktu": "mengatur & memanfaatkan waktu belajar",
  Motivasi: "dorongan & semangat untuk belajar",
};
const bandLabel = (s) =>
  s >= 75 ? "tergolong kuat — pertahankan" :
  s >= 50 ? "cukup baik — masih bisa ditingkatkan" :
  s >= 25 ? "perlu dilatih lebih rutin" :
  "perlu perhatian khusus & latihan bertahap";

function SectionTitle({ icon: Ic, children, no }) {
  return (
    <div className="flex items-center gap-3 mb-4">
      {no && <span className="h-8 w-8 rounded-full bg-[#E25E3E] text-white grid place-items-center font-heading font-black text-sm shrink-0">{no}</span>}
      <Ic className="h-5 w-5 text-[#E25E3E]" />
      <h2 className="font-heading font-black text-2xl text-[#1A1C1A]">{children}</h2>
    </div>
  );
}

export default function HasilAsesmen() {
  const { user } = useAuth();
  const [result, setResult] = useState(null);
  const [fullStatus, setFullStatus] = useState(null);
  const [partnerReport, setPartnerReport] = useState(null);
  const [partnerTryouts, setPartnerTryouts] = useState([]);
  const [insight, setInsight] = useState(null);
  const [loading, setLoading] = useState(true);
  const [selectedProfesi, setSelectedProfesi] = useState(null);
  const [favorite, setFavorite] = useState(null);
  const [journey, setJourney] = useState(null);
  const [recognize, setRecognize] = useState(null);
  const [posttest, setPosttest] = useState(null);
  const [inAppTryouts, setInAppTryouts] = useState([]);
  const [monthlyAvail, setMonthlyAvail] = useState(false);

  const isFav = (p) => favorite && p && favorite.nama === p.nama;
  const toggleFavorite = async (p) => {
    try {
      if (isFav(p)) { await api.delete("/profesi/favorite"); setFavorite(null); toast.success("Dihapus dari profesi impian"); }
      else { const r = await api.post("/profesi/favorite", p); setFavorite(r.data.favorite); toast.success("Disimpan sebagai profesi impian — langkahnya diprioritaskan di Susun Langkah"); }
    } catch { toast.error("Gagal menyimpan"); }
  };

  useEffect(() => {
    api.get("/assessment/result").then((r) => setResult(r.data.result)).catch(() => {}).finally(() => setLoading(false));
    api.get("/profesi/favorite").then((r) => setFavorite(r.data.favorite)).catch(() => {});
    api.get("/journey/me").then((r) => setJourney(r.data.journey)).catch(() => {});
    api.get("/assessment/full/status").then((r) => setFullStatus(r.data)).catch(() => {});
    api.get("/assessment/partner").then((r) => setPartnerReport(r.data.report)).catch(() => {});
    api.get("/partner/tryouts").then((r) => setPartnerTryouts(r.data.items || [])).catch(() => {});
    api.get("/partner/insight").then((r) => setInsight(r.data.insight)).catch(() => {});
    api.get("/learning/recognize").then((r) => setRecognize(r.data)).catch(() => {});
    api.get("/learning/posttest").then((r) => setPosttest(r.data)).catch(() => {});
    api.get("/tryout/results/all").then((r) => setInAppTryouts(r.data.results || [])).catch(() => {});
    api.get("/report/monthly/status").then((r) => setMonthlyAvail(r.data.available)).catch(() => {});
  }, []);

  if (loading) return <AppShell><p className="text-[#5C5F5C]">Memuat hasil…</p></AppShell>;

  const s = result?.scores;
  const n = result?.narrative || {};
  const citaCita = result?.cita_cita;
  const tipe = n.tipe;
  const printSurat = () => {
    const surat = (n.surat_ortu || "").replace(/</g, "&lt;");
    const w = window.open("", "_blank");
    if (!w) { toast.error("Izinkan pop-up untuk mencetak surat"); return; }
    w.document.write(`<html><head><title>Surat untuk Orang Tua — TalentaPath</title><meta charset="utf-8"><style>body{font-family:Georgia,'Times New Roman',serif;max-width:640px;margin:48px auto;padding:0 28px;color:#1A1C1A;line-height:1.8}h1{color:#E25E3E;font-family:Arial,sans-serif;margin:0}.meta{color:#5C5F5C;font-size:13px;margin:2px 0 24px;font-family:Arial,sans-serif}pre{white-space:pre-wrap;font-family:inherit;font-size:16px}</style></head><body><h1>TalentaPath</h1><p class="meta">Surat untuk Orang Tua · ${result.user?.name || ""}</p><pre>${surat}</pre></body></html>`);
    w.document.close(); w.focus(); setTimeout(() => w.print(), 300);
  };
  const kognitif = s ? s.kognitif.map((k) => ({ dimensi: k.dimensi, skor: k.skor })) : [];
  const karakter = s ? s.karakter.map((k) => ({ dimensi: k.dimensi.split(" ")[0], skor: k.skor })) : [];
  const belajar = s ? s.belajar.map((b) => ({ dimensi: b.dimensi, skor: b.skor })) : [];

  return (
    <AppShell>
      <div className="mb-8 flex flex-wrap items-end justify-between gap-4">
        <div>
          <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-[#4A6B53]/10 text-[#4A6B53] text-sm font-medium mb-3"><UserRound className="h-4 w-4" /> Profilku</div>
          <h1 className="font-heading font-black text-4xl sm:text-5xl text-[#1A1C1A] leading-none" data-testid="profilku-nama-siswa">{result?.user?.name || user?.name || "Profilku"}</h1>
        </div>
        <div className="flex items-center gap-3 flex-wrap">
          <button onClick={async () => { try { await downloadFile("/report/pdf", "Rapor-Potensi.pdf"); toast.success("Rapor PDF diunduh"); } catch { toast.error("Unduh gagal"); } }} data-testid="download-pdf-btn"
            className="px-5 py-2.5 rounded-full bg-[#4A6B53] text-white text-sm font-medium hover:opacity-90 transition-opacity flex items-center gap-2">
            <Download className="h-4 w-4" /> Unduh Rapor PDF
          </button>
          {(() => {
            const ready = monthlyAvail;
            return (
              <button disabled={!ready} data-testid="download-monthly-btn"
                onClick={async () => { try { await downloadFile("/report/monthly/pdf", "Laporan-Bulanan.pdf"); toast.success("Laporan Bulanan diunduh"); } catch (e) { toast.error(e.response?.data?.detail || "Unduh gagal"); } }}
                title={ready ? "Unduh Laporan Bulanan Progress" : "Laporan bulanan belum dipublikasikan admin"}
                className={`px-5 py-2.5 rounded-full text-sm font-medium flex items-center gap-2 transition-colors ${ready ? "bg-[#E25E3E] text-white hover:bg-[#C94F31]" : "bg-[#E5E0D8] text-[#9C9A94] cursor-not-allowed"}`}>
                <FileText className="h-4 w-4" /> Laporan Bulanan{ready ? "" : " (belum tersedia)"}
              </button>
            );
          })()}
          <Link to="/asesmen" className="text-sm text-[#E25E3E] font-medium">Ulangi asesmen →</Link>
        </div>
      </div>

      <JourneyPosition current="profil" note={
        <p className="text-sm text-[#1A1C1A] leading-relaxed">
          <span className="font-semibold">Kamu ada di sini sekarang.</span> Ini rangkuman potensimu. Pahami tipe belajar & bidang yang cocok, lalu lanjut <span className="font-semibold">Jelajahi Bidang</span> dan <span className="font-semibold">Susun Langkah</span> untuk mewujudkannya. 🌱
        </p>
      } />

      {result && fullStatus && !fullStatus.unlocked && result.variant !== "lengkap" && (
        <div className="clay-card p-6 mb-8 bg-[#F4B942]/10 border-[#F4B942]/40" data-testid="upsell-full">
          <div className="flex flex-wrap items-center justify-between gap-4">
            <div className="flex items-start gap-3">
              <span className="h-11 w-11 rounded-xl bg-[#1A1C1A] grid place-items-center shrink-0"><Sparkles className="h-5 w-5 text-[#F4B942]" /></span>
              <div>
                <h3 className="font-heading font-black text-lg text-[#1A1C1A]">Buka Asesmen Pro</h3>
                <p className="text-sm text-[#5C5F5C] max-w-xl">Dapatkan estimasi IQ, tes kepribadian Big Five (OCEAN), kecocokan banyak prodi + estimasi peluang, dan rencana studi 3–6 bulan — analisis yang jauh lebih mendalam.</p>
              </div>
            </div>
            <div className="flex items-center gap-3">
              <span className="font-heading font-black text-xl text-[#1A1C1A]">Rp{Number(fullStatus.price).toLocaleString("id-ID")}</span>
              <Link to="/asesmen?full=1" data-testid="upsell-full-btn" className="px-5 py-2.5 rounded-full bg-[#E25E3E] text-white text-sm font-medium hover:bg-[#C94F31] transition-colors inline-flex items-center gap-2"><Sparkles className="h-4 w-4" /> Lihat & Beli</Link>
            </div>
          </div>
        </div>
      )}

      <TalentaCTA className="mb-8" sub="Perkuat area pengembanganmu lewat bimbingan belajar, latihan soal & TryOut bareng mentor di Bimbel Talenta." />

      {/* ===== BAGIAN 1 — PROFIL DIRI (ASESMEN) ===== */}
      <BagianHeader no="1" title="Profil Diri (Asesmen)" desc="Asesmen membantu kamu mengenali diri sendiri — minat, kekuatan, dan cara belajar — sebagai dasar untuk menentukan arah studi dan cita-cita." />
      <PartnerReport report={partnerReport} />
      {(result || partnerReport) && <SystemInsight insight={insight} />}
      {!result && !partnerReport && <EmptyBox label="Belum ada data asesmen" hint="Ikuti Asesmen Pro di aplikasi atau minta admin mengunggah laporan iTi mitra untuk melihat profil lengkapmu." />}
      {partnerReport && !result && (
        <div className="clay-card p-5 mb-8 bg-[#F4B942]/10 border-[#F4B942]/40 flex flex-wrap items-center justify-between gap-3" data-testid="asesmen-cta">
          <p className="text-sm text-[#1A1C1A]">Ikuti juga <b>Asesmen Potensi Diri</b> di aplikasi untuk gambaran tipe belajar & bidang yang lebih lengkap.</p>
          <Link to="/asesmen" className="px-5 py-2.5 rounded-full bg-[#E25E3E] text-white text-sm font-medium hover:bg-[#C94F31] transition-colors inline-flex items-center gap-2"><Sparkles className="h-4 w-4" /> Mulai Asesmen</Link>
        </div>
      )}
      {result && (<>

      {/* Tipe kepribadian belajar — identitas */}
      {tipe?.nama && (
        <div className="clay-card p-7 mb-6 bg-[#E25E3E] text-white border-[#E25E3E]" data-testid="tipe-belajar-hero">
          <div className="flex items-center gap-2 text-white/80 text-sm mb-2"><Sparkles className="h-4 w-4" /> Tipe Kepribadian Belajarmu</div>
          <h2 className="font-heading font-black text-3xl sm:text-4xl mb-2">{tipe.nama}</h2>
          <p className="text-white/90 leading-relaxed max-w-2xl">{tipe.deskripsi}</p>
        </div>
      )}

      {/* Narrative summary */}
      <div className="clay-card p-7 bg-[#1A1C1A] text-white border-[#1A1C1A] mb-8">
        <h2 className="font-heading font-black text-xl mb-2">Ringkasan</h2>
        <p className="text-white/90 leading-relaxed">{n.ringkasan}</p>
      </div>

      {/* Target Pendidikan Impian (dari Susun Langkah) */}
      {journey?.target_pendidikan && (journey.target_pendidikan.prodi || journey.target_pendidikan.ptn) && (
        <div className="clay-card p-6 mb-8 bg-[#4A6B53]/5 border-[#4A6B53]/25" data-testid="target-pendidikan-card">
          <div className="flex flex-wrap items-center justify-between gap-3">
            <div>
              <div className="flex items-center gap-2 text-sm font-semibold text-[#4A6B53] mb-1"><GraduationCap className="h-4 w-4" /> Target Pendidikan Impianku</div>
              <p className="font-heading font-black text-xl text-[#1A1C1A]">
                {journey.target_pendidikan.prodi || "—"}{journey.target_pendidikan.ptn ? ` · ${journey.target_pendidikan.ptn}` : ""}
              </p>
            </div>
            <Link to="/susun-langkah" className="text-sm text-[#E25E3E] font-medium flex items-center gap-1.5 shrink-0">Ubah target <ArrowRight className="h-4 w-4" /></Link>
          </div>
        </div>
      )}

      {/* ===== 1. Analisa Potensi & Kekuatan Kognitif ===== */}
      <div className="mb-10" data-testid="section-potensi">
        <SectionTitle icon={TrendingUp} no="1">Analisa Potensi & Kekuatan Kognitif</SectionTitle>
        <div className="grid md:grid-cols-2 gap-6 mb-6">
          <div className="clay-card p-6">
            <h3 className="font-heading font-bold text-lg text-[#1A1C1A] mb-4 flex items-center gap-2"><TrendingUp className="h-5 w-5 text-[#4A6B53]" /> Kekuatan Utama</h3>
            <ul className="space-y-2.5">
              {n.kekuatan.map((k, i) => <li key={i} className="flex items-start gap-2 text-sm text-[#1A1C1A]"><span className="text-[#4A6B53] mt-0.5">✓</span> {k}</li>)}
            </ul>
          </div>
          <div className="clay-card p-6">
            <h3 className="font-heading font-bold text-lg text-[#1A1C1A] mb-4 flex items-center gap-2"><AlertCircle className="h-5 w-5 text-[#F4B942]" /> Area Pengembangan</h3>
            <ul className="space-y-2.5">
              {n.pengembangan.map((k, i) => <li key={i} className="flex items-start gap-2 text-sm text-[#1A1C1A]"><span className="text-[#F4B942] mt-0.5">→</span> {k}</li>)}
            </ul>
          </div>
        </div>
        <div className="grid md:grid-cols-2 gap-6">
          <div className="clay-card p-6">
            <h3 className="font-heading font-bold text-lg text-[#1A1C1A] mb-1">Kekuatan Kognitif</h3>
            <p className="text-sm text-[#5C5F5C] mb-4">Rata-rata {s.kognitif_overall}%</p>
            <ResponsiveContainer width="100%" height={260}>
              <RadarChart data={kognitif} outerRadius={90}>
                <PolarGrid stroke="#E5E0D8" />
                <PolarAngleAxis dataKey="dimensi" tick={{ fill: "#5C5F5C", fontSize: 12 }} />
                <Radar dataKey="skor" stroke="#E25E3E" fill="#E25E3E" fillOpacity={0.35} />
              </RadarChart>
            </ResponsiveContainer>
            <div className="mt-3 border-t border-[#E5E0D8] pt-3 space-y-1" data-testid="kognitif-legend">
              <p className="text-xs font-semibold text-[#1A1C1A] mb-1">Apa arti grafik ini?</p>
              {kognitif.map((k) => (
                <p key={k.dimensi} className="text-xs text-[#5C5F5C] leading-relaxed">
                  <span className="font-semibold text-[#1A1C1A]">{k.dimensi} ({k.skor}%)</span> — {KOGNITIF_INFO[k.dimensi] || "kemampuan berpikir"}; {bandLabel(k.skor)}.
                </p>
              ))}
              {s.kognitif_overall === 0 && (
                <p className="text-xs text-[#D9381E] leading-relaxed mt-1">Skor 0% biasanya karena jawaban tes kognitif belum tepat atau terlewat saat mengklik cepat — ini <b>bukan</b> vonis kemampuanmu. Coba <Link to="/asesmen" className="underline font-medium">ulangi asesmen</Link> dengan tenang.</p>
              )}
            </div>
          </div>
          <div className="clay-card p-6">
            <h3 className="font-heading font-bold text-lg text-[#1A1C1A] mb-4">Profil Minat (RIASEC)</h3>
            <ResponsiveContainer width="100%" height={260}>
              <BarChart data={karakter} layout="vertical" margin={{ left: 10 }}>
                <XAxis type="number" domain={[0, 100]} hide />
                <YAxis type="category" dataKey="dimensi" width={90} tick={{ fill: "#5C5F5C", fontSize: 12 }} />
                <Bar dataKey="skor" radius={[0, 6, 6, 0]}>
                  {karakter.map((_, i) => <Cell key={i} fill={i === 0 ? "#E25E3E" : i === 1 ? "#4A6B53" : "#F4B942"} />)}
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          </div>
        </div>
      </div>

      {/* ===== 2. Gaya Belajar ===== */}
      <div className="mb-10" data-testid="section-gaya-belajar">
        <SectionTitle icon={Lightbulb} no="2">Gaya Belajar</SectionTitle>
        {n.gaya_belajar && (
          <div className="clay-card p-6 mb-6 bg-[#4A6B53]/5 border-[#4A6B53]/20">
            <p className="text-[#1A1C1A] leading-relaxed">{n.gaya_belajar}</p>
          </div>
        )}
        <div className="clay-card p-6">
          <h3 className="font-heading font-bold text-lg text-[#1A1C1A] mb-4">Kebiasaan Belajar</h3>
          <ResponsiveContainer width="100%" height={220}>
            <BarChart data={belajar}>
              <XAxis dataKey="dimensi" tick={{ fill: "#5C5F5C", fontSize: 12 }} />
              <YAxis domain={[0, 100]} tick={{ fill: "#5C5F5C", fontSize: 11 }} />
              <Bar dataKey="skor" fill="#4A6B53" radius={[6, 6, 0, 0]} />
            </BarChart>
          </ResponsiveContainer>
          <div className="mt-3 border-t border-[#E5E0D8] pt-3 space-y-1" data-testid="belajar-legend">
            <p className="text-xs font-semibold text-[#1A1C1A] mb-1">Apa arti grafik ini?</p>
            {belajar.map((b) => (
              <p key={b.dimensi} className="text-xs text-[#5C5F5C] leading-relaxed">
                <span className="font-semibold text-[#1A1C1A]">{b.dimensi} ({b.skor}%)</span> — {BELAJAR_INFO[b.dimensi] || "kebiasaan belajarmu"}; {bandLabel(b.skor)}.
              </p>
            ))}
          </div>
        </div>
      </div>

      {/* ===== Analisis Lengkap (Premium) ===== */}
      {result.variant === "lengkap" && (
        <div className="mb-10" data-testid="section-lengkap">
          <SectionTitle icon={Sparkles} no="★">Analisis Lengkap (Premium)</SectionTitle>
          <div className="grid md:grid-cols-2 gap-6 mb-6">
            {s.iq_estimasi && (
              <div className="clay-card p-6 bg-[#1A1C1A] text-white border-[#1A1C1A]" data-testid="iq-card">
                <p className="text-white/70 text-sm mb-1">Estimasi Kemampuan Kognitif (IQ)</p>
                <div className="flex items-end gap-3">
                  <span className="font-heading font-black text-5xl text-[#F4B942]">{s.iq_estimasi.skor}</span>
                  <span className="text-white/80 mb-1">{s.iq_estimasi.band}</span>
                </div>
                {n.iq_analisa && <p className="text-white/80 text-sm mt-3 leading-relaxed">{n.iq_analisa}</p>}
              </div>
            )}
            <div className="clay-card p-6" data-testid="ocean-card">
              <h3 className="font-heading font-bold text-lg text-[#1A1C1A] mb-3">Kepribadian (Big Five / OCEAN)</h3>
              <div className="space-y-2">
                {(s.ocean || []).map((o) => (
                  <div key={o.kode}>
                    <div className="flex justify-between text-xs mb-0.5"><span className="text-[#1A1C1A] font-medium">{o.dimensi}</span><span className="text-[#5C5F5C]">{o.skor}%</span></div>
                    <div className="h-2 rounded-full bg-[#F0ECE4] overflow-hidden"><div className="h-full bg-[#4A6B53] rounded-full" style={{ width: `${o.skor}%` }} /></div>
                  </div>
                ))}
              </div>
              {n.kepribadian && <p className="text-sm text-[#5C5F5C] mt-3 leading-relaxed">{n.kepribadian}</p>}
            </div>
          </div>
          {n.prodi_match?.length > 0 && (
            <div className="clay-card p-6 mb-6" data-testid="prodi-match">
              <h3 className="font-heading font-bold text-lg text-[#1A1C1A] mb-3">Kecocokan Prodi & Estimasi Peluang</h3>
              <div className="space-y-2">
                {n.prodi_match.map((p, i) => (
                  <div key={i} className="flex items-center justify-between gap-2 text-sm">
                    <span className="text-[#1A1C1A]">{p.prodi} <span className="text-[#5C5F5C]">· {p.bidang}</span></span>
                    <span className="flex items-center gap-2 shrink-0">
                      <span className="text-[#5C5F5C]">{p.match}%</span>
                      <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${p.peluang === "Tinggi" ? "bg-[#4A6B53]/15 text-[#4A6B53]" : p.peluang === "Sedang" ? "bg-[#F4B942]/20 text-[#8a6d16]" : "bg-[#D9381E]/10 text-[#D9381E]"}`}>{p.peluang}</span>
                    </span>
                  </div>
                ))}
              </div>
            </div>
          )}
          {n.rencana_studi?.length > 0 && (
            <div className="clay-card p-6 bg-[#4A6B53]/5 border-[#4A6B53]/25" data-testid="rencana-studi">
              <h3 className="font-heading font-bold text-lg text-[#1A1C1A] mb-3">Rencana Studi 3–6 Bulan</h3>
              <ol className="space-y-2">
                {n.rencana_studi.map((r, i) => (<li key={i} className="flex items-start gap-2 text-sm text-[#1A1C1A]"><span className="h-5 w-5 rounded-full bg-[#4A6B53] text-white grid place-items-center text-xs shrink-0">{i + 1}</span> {r}</li>))}
              </ol>
            </div>
          )}
        </div>
      )}

      {/* ===== 3. Cita-cita / Impian ===== */}
      <div className="mb-10" data-testid="section-cita-cita">
        <SectionTitle icon={Star} no="3">Cita-cita / Impianmu</SectionTitle>
        <div className="clay-card p-7 bg-[#F4B942]/10 border-[#F4B942]/30">
          {citaCita ? (
            <>
              <div className="flex items-center gap-3 mb-2">
                <span className="h-11 w-11 rounded-xl bg-[#F4B942] text-[#1A1C1A] grid place-items-center"><Star className="h-5 w-5" /></span>
                <span className="font-heading font-black text-2xl text-[#1A1C1A]">{citaCita}</span>
              </div>
              {n.cita_cita_analisa && <p className="text-[#5C5F5C] leading-relaxed mt-2">{n.cita_cita_analisa}</p>}
            </>
          ) : (
            <p className="text-[#5C5F5C]">Kamu belum menuliskan cita-cita. <Link to="/asesmen" className="text-[#E25E3E] font-medium">Ulangi asesmen</Link> untuk menambahkannya agar rekomendasi lebih terarah.</p>
          )}
        </div>
      </div>

      {/* ===== Sesuaikan dengan cita-cita (rekonsiliasi) ===== */}
      <ReconcileCareer />

      {/* ===== 4. Anjuran Mapel Prioritas / Arahan Rumpun ===== */}
      {(n.mapel_prioritas?.length > 0 || n.arah_smp) && (
        <div className="mb-10" data-testid="section-mapel">
          <SectionTitle icon={BookOpen} no="4">Anjuran Mapel Prioritas</SectionTitle>
          {user?.jenjang === "SMP" && n.arah_smp && (
            <div className="clay-card p-7 bg-[#1A1C1A] text-white border-[#1A1C1A] mb-5" data-testid="arah-smp">
              <p className="text-white/70 text-sm mb-1">Rumpun paling cocok untukmu</p>
              <h3 className="font-heading font-black text-2xl mb-2 text-[#F4B942]">{n.arah_smp.rumpun}</h3>
              <p className="text-white/90 leading-relaxed mb-3">{n.arah_smp.alasan}</p>
              <div className="grid sm:grid-cols-2 gap-4">
                <div className="bg-white/10 rounded-xl p-4"><p className="text-[#F4B942] font-semibold text-sm mb-1">Optimalkan kekuatan</p><p className="text-white/85 text-sm">{n.arah_smp.optimalkan}</p></div>
                <div className="bg-white/10 rounded-xl p-4"><p className="text-[#F4B942] font-semibold text-sm mb-1">Minimalkan kelemahan</p><p className="text-white/85 text-sm">{n.arah_smp.minimalkan}</p></div>
              </div>
            </div>
          )}
          {n.mapel_prioritas?.length > 0 && (
            <div className="grid sm:grid-cols-3 gap-5">
              {n.mapel_prioritas.map((m, i) => (
                <div key={i} className="clay-card p-6" data-testid={`mapel-prioritas-${i}`}>
                  <span className="h-10 w-10 rounded-xl bg-[#E25E3E] text-white grid place-items-center mb-3"><BookOpen className="h-5 w-5" /></span>
                  <h3 className="font-heading font-bold text-lg text-[#1A1C1A]">{m.mapel}</h3>
                  <p className="text-sm text-[#5C5F5C] mt-1">{m.alasan}</p>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* ===== 5. Anjuran Jalur Pendidikan Lanjutan ===== */}
      {(n.jalur_rekomendasi || n.utbk_subtes?.length > 0 || n.prodi_rekomendasi?.length > 0) && (
        <div className="mb-10" data-testid="section-jalur">
          <SectionTitle icon={Route} no="5">Anjuran Jalur Pendidikan Lanjutan</SectionTitle>
          {n.jalur_rekomendasi && (
            <div className="clay-card p-7 bg-[#4A6B53] text-white border-[#4A6B53] mb-5" data-testid="jalur-rekomendasi">
              <p className="text-white/70 text-sm mb-1">Jalur paling sesuai</p>
              <h3 className="font-heading font-black text-2xl mb-2">{n.jalur_rekomendasi.nama}</h3>
              <p className="text-white/90 leading-relaxed">{n.jalur_rekomendasi.alasan}</p>
              {n.jalur_rekomendasi.alternatif && <p className="text-white/70 text-sm mt-2">Alternatif: {n.jalur_rekomendasi.alternatif}</p>}
              <Link to="/info-jalur" className="mt-4 inline-flex items-center gap-2 px-5 py-2.5 rounded-full bg-[#F4B942] text-[#1A1C1A] text-sm font-semibold hover:bg-[#e5ad35] transition-colors">
                Lihat syarat & link resmi <ArrowRight className="h-4 w-4" />
              </Link>
            </div>
          )}
          {Number(user?.kelas) === 12 && n.utbk_subtes?.length > 0 && (
            <div className="clay-card p-6 mb-5">
              <h3 className="font-heading font-bold text-lg text-[#1A1C1A] mb-4">Subtes UTBK yang Perlu Diperkuat</h3>
              <div className="grid sm:grid-cols-3 gap-4">
                {n.utbk_subtes.map((u, i) => (
                  <div key={i} className="rounded-xl border border-[#E5E0D8] p-4" data-testid={`utbk-subtes-${i}`}>
                    <p className="font-semibold text-[#1A1C1A]">{u.subtes}</p>
                    <p className="text-sm text-[#5C5F5C] mt-1">{u.alasan}</p>
                  </div>
                ))}
              </div>
            </div>
          )}
          {n.prodi_rekomendasi?.length > 0 && (
            <div className="clay-card p-6">
              <h3 className="font-heading font-bold text-lg text-[#1A1C1A] mb-3">Prodi yang Sesuai</h3>
              <div className="flex flex-wrap gap-2">
                {n.prodi_rekomendasi.map((p, i) => <span key={i} className="px-3 py-1.5 rounded-full bg-[#E25E3E]/10 text-[#E25E3E] text-sm font-medium" data-testid={`prodi-rek-${i}`}>{p}</span>)}
              </div>
            </div>
          )}
        </div>
      )}

      {/* ===== Profesi & Karier yang Paling Sesuai ===== */}
      {n.profesi?.length > 0 && (
        <div className="mb-10" data-testid="section-profesi">
          <SectionTitle icon={Briefcase}>Profesi & Karier yang Paling Sesuai</SectionTitle>
          <p className="text-[#5C5F5C] -mt-2 mb-5 max-w-2xl">Gambaran nyata kamu bisa jadi apa nanti — dipilih dari kombinasi potensi, kekuatan kognitif, gaya belajar, dan cita-citamu.</p>
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
            {n.profesi.map((p, i) => (
              <button key={i} onClick={() => setSelectedProfesi(p)} data-testid={`profesi-${i}`}
                className="clay-card clay-card-hover p-6 text-left w-full">
                <div className="flex items-center justify-between mb-3">
                  <span className="h-11 w-11 rounded-xl bg-[#1A1C1A] text-white grid place-items-center"><Briefcase className="h-5 w-5" /></span>
                  <span className="text-xs font-semibold text-[#E25E3E] flex items-center gap-1">Peta karier <ArrowRight className="h-3.5 w-3.5" /></span>
                </div>
                <h3 className="font-heading font-bold text-lg text-[#1A1C1A] flex items-center gap-2">{p.nama}{isFav(p) && <Star className="h-4 w-4 text-[#F4B942] fill-[#F4B942]" data-testid={`profesi-fav-star-${i}`} />}</h3>
                <div className="mt-3 space-y-2.5 text-sm">
                  <p className="text-[#5C5F5C]"><span className="font-semibold text-[#1A1C1A]">Sehari-hari:</span> {p.sehari_hari}</p>
                  <p className="text-[#5C5F5C]"><span className="font-semibold text-[#4A6B53]">Kenapa cocok:</span> {p.kenapa_cocok}</p>
                  <p className="text-[#5C5F5C]"><span className="font-semibold text-[#E25E3E]">Prospek:</span> {p.prospek}</p>
                </div>
              </button>
            ))}
          </div>
        </div>
      )}

      {/* Rekomendasi bidang (kartu %) */}
      <div className="mb-10">
        <SectionTitle icon={Compass}>Bidang & Jurusan yang Sesuai</SectionTitle>
        <div className="grid sm:grid-cols-3 gap-5">
          {s.rekomendasi.map((b) => (
            <div key={b.id} className="clay-card clay-card-hover p-6">
              <div className="flex items-center justify-between mb-3">
                <span className="h-11 w-11 rounded-xl grid place-items-center text-white" style={{ background: b.warna }}><Icon name={b.ikon} className="h-5 w-5" /></span>
                <span className="font-heading font-black text-2xl" style={{ color: b.warna }}>{b.match}%</span>
              </div>
              <h3 className="font-heading font-bold text-lg text-[#1A1C1A]">{b.nama}</h3>
              <p className="text-sm text-[#5C5F5C] mt-2"><span className="font-semibold text-[#1A1C1A]">Jurusan:</span> {b.jurusan.join(", ")}</p>
              <p className="text-sm text-[#5C5F5C] mt-1"><span className="font-semibold text-[#1A1C1A]">Mapel:</span> {b.mapel_pendukung.join(", ")}</p>
            </div>
          ))}
        </div>
      </div>

      {/* Jalur kuesioner (SMA 11-12) — skor kuesioner arah */}
      {s.jalur && s.jalur.length > 0 && (
        <div className="mb-10" data-testid="jalur-section">
          <SectionTitle icon={Milestone}>Kecocokan Arah Pendidikan (dari kuesioner)</SectionTitle>
          <div className="grid sm:grid-cols-3 gap-5">
            {s.jalur.map((j) => {
              const JIcon = JALUR_ICON[j.kode] || Milestone;
              const color = JALUR_COLOR[j.kode] || "#E25E3E";
              return (
                <div key={j.kode} className="clay-card p-6" data-testid={`jalur-card-${j.kode}`}>
                  <div className="flex items-center justify-between mb-3">
                    <span className="h-11 w-11 rounded-xl grid place-items-center text-white" style={{ background: color }}><JIcon className="h-5 w-5" /></span>
                    <span className="font-heading font-black text-2xl" style={{ color }}>{j.skor}%</span>
                  </div>
                  <h3 className="font-heading font-bold text-base text-[#1A1C1A] leading-snug">{j.label}</h3>
                  <div className="h-2 rounded-full bg-[#F0ECE4] overflow-hidden mt-3 mb-3">
                    <div className="h-full rounded-full transition-all" style={{ width: `${j.skor}%`, background: color }} />
                  </div>
                  <p className="text-xs text-[#5C5F5C]">{j.contoh}</p>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* ===== Surat untuk Orang Tua ===== */}
      {n.surat_ortu && (
        <div className="mb-10" data-testid="section-surat-ortu">
          <SectionTitle icon={Mail}>Surat untuk Orang Tua</SectionTitle>
          <div className="clay-card p-7 bg-[#FDFBF7] border-[#E5E0D8]">
            <div className="border-l-4 border-[#E25E3E] pl-5">
              <p className="text-[#1A1C1A] leading-8 whitespace-pre-line" style={{ fontFamily: "Georgia, serif" }} data-testid="surat-ortu-text">{n.surat_ortu}</p>
            </div>
            <div className="mt-6 flex flex-wrap gap-3">
              <button onClick={printSurat} data-testid="print-surat-btn"
                className="px-5 py-2.5 rounded-full bg-[#1A1C1A] text-white text-sm font-medium hover:opacity-90 transition-opacity flex items-center gap-2">
                <Printer className="h-4 w-4" /> Cetak / Simpan Surat
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Next steps */}
      <div className="clay-card p-7 bg-[#1A1C1A] text-white border-[#1A1C1A]">
        <h3 className="font-heading font-black text-xl mb-3">Langkah Berikutnya</h3>
        <ul className="space-y-2 mb-5">
          {n.saran_langkah.map((a, i) => <li key={i} className="flex items-start gap-2 text-white/90 text-sm"><span className="text-[#F4B942]">→</span> {a}</li>)}
        </ul>
        <div className="flex flex-wrap gap-3">
          <Link to="/jelajahi-bidang" className="px-5 py-2.5 rounded-full bg-white text-[#1A1C1A] text-sm font-medium hover:bg-white/90 transition-colors flex items-center gap-1.5">Jelajahi Bidang <ArrowRight className="h-4 w-4" /></Link>
          <Link to="/susun-langkah" className="px-5 py-2.5 rounded-full bg-[#E25E3E] text-white text-sm font-medium hover:bg-[#C94F31] transition-colors flex items-center gap-1.5">Susun Langkah <ArrowRight className="h-4 w-4" /></Link>
          <Link to="/tryout" className="px-5 py-2.5 rounded-full bg-white/15 text-white text-sm font-medium hover:bg-white/25 transition-colors">Ikuti TryOut</Link>
        </div>
      </div>
      </>)}

      {/* ===== BAGIAN 2 — RECOGNIZE (DIAGNOSTIK) ===== */}
      <BagianHeader no="2" title="Recognize (Diagnostik Awal)" desc="Diagnostik awal (sekali) sebelum program belajar — posisi awal kemampuan & hal yang perlu perhatian." />
      <RecognizeSection report={recognize?.report} analisa={recognize?.analisa} />

      {/* ===== BAGIAN 3 — POSTES HARIAN ===== */}
      <div className="mt-6">
        <BagianHeader no="3" title="Postes Harian" desc="Hasil postes harian berurutan per tanggal — progres belajar dari hari ke hari (pretest → posttest)." />
        <PosttestSection items={posttest?.items} analisa={posttest?.analisa} />
      </div>

      {/* ===== BAGIAN 4 — TRYOUT ===== */}
      <div className="mt-6 mb-10">
        <BagianHeader no="4" title="TryOut" desc="TryOut membantu mengukur sejauh mana kesiapan belajarmu. Ikuti TryOut untuk melihat perkembangan nilaimu dari waktu ke waktu." />
        <a href="https://bimbeltalenta.id/" target="_blank" rel="noopener noreferrer" data-testid="tryout-talenta-link" className="inline-flex items-center gap-2 mb-4 px-5 py-2.5 rounded-full bg-[#E25E3E] text-white text-sm font-medium hover:bg-[#C94F31] transition-colors">
          <ExternalLink className="h-4 w-4" /> Ikuti TryOut di Bimbel Talenta
        </a>
        {(() => {
          const realTryouts = (partnerTryouts || []).filter((t) => !/postes|recognize|baseline|pretest|postest|diagnos/i.test(t.batch_name || ""));
          const hasTryout = realTryouts.length > 0 || inAppTryouts.length > 0;
          return hasTryout ? (
            <>
              <TryoutInsight insight={insight} />
              <PartnerTryouts items={realTryouts} />
              <InAppTryouts items={inAppTryouts} />
              {(() => {
                const partnerScores = realTryouts.map((t) => t?.hasil?.persen_benar).filter((n) => typeof n === "number");
                const appScores = (inAppTryouts || []).map((t) => t?.skor).filter((n) => typeof n === "number");
                const all = [...partnerScores, ...appScores];
                if (!all.length) return null;
                const avg = Math.round(all.reduce((a, b) => a + b, 0) / all.length);
                return <MotivasiQuote skor={avg} className="mt-4" />;
              })()}
            </>
          ) : (
            <EmptyBox label="Belum ada data TryOut" hint="Ikuti TryOut untuk melihat kesiapan belajar & perkembangan nilaimu dari waktu ke waktu." />
          );
        })()}
      </div>

      {/* ===== Modal Peta Karier ===== */}
      {selectedProfesi && (() => {
        const steps = (selectedProfesi.langkah && selectedProfesi.langkah.length > 0) ? selectedProfesi.langkah : [
          n.mapel_prioritas?.length ? `Kuatkan mapel: ${n.mapel_prioritas.map((m) => m.mapel).join(", ")}` : "Kuatkan mapel inti yang sesuai bidangmu",
          "Ikuti kegiatan, ekskul, atau lomba yang relevan",
          "Bangun portofolio atau proyek nyata di bidang ini",
          s.rekomendasi?.[0] ? `Targetkan prodi seperti ${s.rekomendasi[0].jurusan.slice(0, 2).join(", ")} di PTN tujuanmu` : "Tentukan prodi & PTN tujuanmu",
        ];
        return (
          <div className="fixed inset-0 z-50 grid place-items-center p-4 bg-black/50" onClick={() => setSelectedProfesi(null)} data-testid="peta-karier-modal">
            <div className="clay-card bg-[#FAF8F5] max-w-lg w-full p-7 relative" onClick={(e) => e.stopPropagation()}>
              <button onClick={() => setSelectedProfesi(null)} data-testid="close-peta-karier" className="absolute top-4 right-4 p-2 rounded-full hover:bg-[#1A1C1A]/5 transition-colors"><X className="h-5 w-5 text-[#5C5F5C]" /></button>
              <span className="h-12 w-12 rounded-xl bg-[#E25E3E] text-white grid place-items-center mb-4"><Route className="h-6 w-6" /></span>
              <p className="text-sm text-[#5C5F5C]">Peta menuju</p>
              <h3 className="font-heading font-black text-2xl text-[#1A1C1A] mb-1">{selectedProfesi.nama}</h3>
              <p className="text-sm text-[#5C5F5C] mb-5">{selectedProfesi.sehari_hari}</p>
              <p className="text-sm font-semibold text-[#1A1C1A] mb-3">Langkah nyata untuk kamu:</p>
              <ol className="space-y-3">
                {steps.map((st, i) => (
                  <li key={i} className="flex items-start gap-3" data-testid={`peta-step-${i}`}>
                    <span className="h-7 w-7 rounded-full bg-[#4A6B53] text-white grid place-items-center text-sm font-bold shrink-0">{i + 1}</span>
                    <span className="text-sm text-[#1A1C1A] pt-0.5">{st}</span>
                  </li>
                ))}
              </ol>
              <div className="mt-6 flex flex-wrap gap-3">
                <button onClick={() => toggleFavorite(selectedProfesi)} data-testid="favorite-profesi-btn"
                  className={`px-5 py-2.5 rounded-full text-sm font-medium flex items-center gap-1.5 transition-colors ${isFav(selectedProfesi) ? "bg-[#F4B942] text-[#1A1C1A] hover:bg-[#e5ad35]" : "bg-[#E25E3E] text-white hover:bg-[#C94F31]"}`}>
                  <Star className={`h-4 w-4 ${isFav(selectedProfesi) ? "fill-[#1A1C1A]" : ""}`} /> {isFav(selectedProfesi) ? "Profesi Impian ✓" : "Jadikan Profesi Impian"}
                </button>
                <Link to="/susun-langkah" className="px-5 py-2.5 rounded-full bg-[#1A1C1A] text-white text-sm font-medium hover:opacity-90 transition-opacity flex items-center gap-1.5">Susun Langkah <ArrowRight className="h-4 w-4" /></Link>
              </div>
            </div>
          </div>
        );
      })()}
    </AppShell>
  );
}
